// src/pages/finance-portal/requisitions/RequisitionDetail.js
//
// Requisition detail with workflow + approval log + convert-to-PO.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, IconButton, Card, Chip, Alert, Skeleton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Autocomplete,
    FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import CancelIcon from '@mui/icons-material/Cancel';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const STATUS_COLORS = {
    DRAFT: { bg: '#f1f5f9', color: '#64748b' },
    SUBMITTED: { bg: '#fef9c3', color: '#854d0e' },
    APPROVED: { bg: '#dcfce7', color: '#166534' },
    REJECTED: { bg: '#fecaca', color: '#991b1b' },
    CONVERTED: { bg: '#e0e7ff', color: '#4338ca' },
    CANCELLED: { bg: '#f1f5f9', color: '#94a3b8' },
};

function fmtMoney(val, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency, maximumFractionDigits: 2,
    }).format(val || 0);
}

export default function RequisitionDetail() {
    const { requisitionId } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [r, setR] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [rejectDialog, setRejectDialog] = useState({ open: false, reason: '' });
    const [cancelDialog, setCancelDialog] = useState({ open: false, reason: '' });
    const [convertDialog, setConvertDialog] = useState({
        open: false, vendor_id: '', expected_delivery_date: '', payment_terms: 'NET_30',
    });
    const [vendors, setVendors] = useState([]);

    const fetch = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/requisitions/${requisitionId}`);
            if (res.data.success) setR(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [requisitionId]);

    const fetchVendors = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/vendors',
                { params: { per_page: 200, status: 'ACTIVE' } });
            if (res.data.success) setVendors(res.data.data || []);
        } catch { /* silent */ }
    }, []);

    useEffect(() => { fetch(); fetchVendors(); }, [fetch, fetchVendors]);

    const transition = async (action, body) => {
        try {
            const res = await axiosInstance.post(
                `/api/finance/requisitions/${requisitionId}/${action}`, body || {});
            enqueueSnackbar(res.data?.message || 'Done', { variant: 'success' });
            fetch();
            return res.data;
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
            return null;
        }
    };

    const submitReject = async () => {
        if (!rejectDialog.reason.trim()) {
            enqueueSnackbar('Reason is required', { variant: 'warning' });
            return;
        }
        await transition('reject', { reason: rejectDialog.reason.trim() });
        setRejectDialog({ open: false, reason: '' });
    };

    const submitCancel = async () => {
        await transition('cancel', { reason: cancelDialog.reason || null });
        setCancelDialog({ open: false, reason: '' });
    };

    const submitConvert = async () => {
        if (!convertDialog.vendor_id) {
            enqueueSnackbar('Vendor is required', { variant: 'warning' });
            return;
        }
        const result = await transition('convert-to-po', {
            vendor_id: convertDialog.vendor_id,
            expected_delivery_date: convertDialog.expected_delivery_date || null,
            payment_terms: convertDialog.payment_terms || 'NET_30',
        });
        setConvertDialog({ open: false, vendor_id: '', expected_delivery_date: '', payment_terms: 'NET_30' });
        if (result?.data?.po?.id) {
            navigate(`/finance-portal/procurement-pos/${result.data.po.id}`);
        }
    };

    const labelCell = { fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b',
                        textTransform: 'uppercase', bgcolor: '#f8fafc',
                        borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' };
    const dataCell = { fontFamily, fontSize: '0.85rem', color: '#475569' };

    if (loading) return <Box sx={{ p: 4, fontFamily }}><Skeleton height={400} /></Box>;
    if (!r) return (
        <Box sx={{ p: 4, fontFamily }}>
            <Alert severity="error">{error || 'Requisition not found'}</Alert>
            <Button onClick={() => navigate('/finance-portal/requisitions')} sx={{ mt: 2, fontFamily }}>
                Back to list
            </Button>
        </Box>
    );

    const sStyle = STATUS_COLORS[r.status] || STATUS_COLORS.DRAFT;
    const isEditable = r.status === 'DRAFT' || r.status === 'REJECTED';

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <IconButton onClick={() => navigate('/finance-portal/requisitions')}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                            Purchase Requisition
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily, fontWeight: 800, color: 'white' }}>
                            {r.requisition_number}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Chip label={r.status.replace('_', ' ')} size="small"
                          sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                bgcolor: sStyle.bg, color: sStyle.color }} />
                    <Chip label={r.urgency} size="small"
                          sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem',
                                bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1200, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {r.rejection_reason && r.status === 'REJECTED' && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>
                        <strong>Rejected:</strong> {r.rejection_reason}
                    </Alert>
                )}

                {r.converted_po_id && (
                    <Alert severity="info" action={
                        <Button size="small" onClick={() => navigate(`/finance-portal/procurement-pos/${r.converted_po_id}`)}
                                sx={{ fontFamily, textTransform: 'none' }}>
                            Open PO
                        </Button>
                    } sx={{ mb: 2, borderRadius: '12px', fontFamily }}>
                        Converted to procurement PO #{r.converted_po_id}
                    </Alert>
                )}

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Box sx={{ display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                              gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8',
                                              textTransform: 'uppercase' }}>Department</Typography>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.95rem' }}>
                                {r.department || '—'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8',
                                              textTransform: 'uppercase' }}>Needed By</Typography>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.95rem' }}>
                                {r.needed_by_date || '—'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8',
                                              textTransform: 'uppercase' }}>Preferred Vendor</Typography>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.95rem' }}>
                                {r.preferred_vendor_name || '—'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8',
                                              textTransform: 'uppercase' }}>Estimated Total</Typography>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                                {fmtMoney(r.estimated_total, r.currency)}
                            </Typography>
                        </Box>
                    </Box>

                    {r.justification && (
                        <Box sx={{ mt: 2 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8',
                                              textTransform: 'uppercase' }}>Justification</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', mt: 0.5 }}>
                                {r.justification}
                            </Typography>
                        </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {isEditable && (
                            <Button startIcon={<EditIcon />} variant="outlined" size="small"
                                    onClick={() => navigate(`/finance-portal/requisitions/${r.id}/edit`)}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                                Edit
                            </Button>
                        )}
                        {r.status === 'DRAFT' && (
                            <Button startIcon={<SendIcon />} variant="contained" size="small"
                                    onClick={() => transition('submit')}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                          borderRadius: '10px', bgcolor: '#22c55e' }}>
                                Submit for Approval
                            </Button>
                        )}
                        {r.status === 'SUBMITTED' && (
                            <>
                                <Button startIcon={<CheckIcon />} variant="contained" size="small"
                                        onClick={() => transition('approve')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                              borderRadius: '10px', bgcolor: '#16a34a' }}>
                                    Approve
                                </Button>
                                <Button startIcon={<BlockIcon />} variant="outlined" size="small"
                                        onClick={() => setRejectDialog({ open: true, reason: '' })}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                              borderRadius: '10px', color: '#ef4444', borderColor: '#fecaca' }}>
                                    Reject
                                </Button>
                            </>
                        )}
                        {r.status === 'APPROVED' && (
                            <Button startIcon={<LocalShippingIcon />} variant="contained" size="small"
                                    onClick={() => setConvertDialog({
                                        open: true,
                                        vendor_id: r.preferred_vendor_id || '',
                                        expected_delivery_date: r.needed_by_date || '',
                                        payment_terms: 'NET_30',
                                    })}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                          borderRadius: '10px', bgcolor: '#3b82f6' }}>
                                Convert to PO
                            </Button>
                        )}
                        {(r.status === 'DRAFT' || r.status === 'SUBMITTED' || r.status === 'APPROVED') && (
                            <Button startIcon={<CancelIcon />} variant="text" size="small"
                                    onClick={() => setCancelDialog({ open: true, reason: '' })}
                                    sx={{ fontFamily, textTransform: 'none', color: '#94a3b8' }}>
                                Cancel
                            </Button>
                        )}
                    </Box>
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2 }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem' }}>
                            Items ({r.lines?.length || 0})
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={labelCell}>Description</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Qty</TableCell>
                                    <TableCell sx={labelCell}>UoM</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Unit Cost</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Line Total</TableCell>
                                    <TableCell sx={labelCell}>GL Account</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(r.lines || []).map(l => (
                                    <TableRow key={l.id}>
                                        <TableCell sx={{ ...dataCell, color: '#1e293b', fontWeight: 500 }}>
                                            {l.description}
                                        </TableCell>
                                        <TableCell sx={{ ...dataCell, textAlign: 'right' }}>{l.quantity}</TableCell>
                                        <TableCell sx={dataCell}>{l.unit_of_measure || '—'}</TableCell>
                                        <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                            {fmtMoney(l.estimated_cost, r.currency)}
                                        </TableCell>
                                        <TableCell sx={{ ...dataCell, textAlign: 'right', fontWeight: 600, color: '#1e293b' }}>
                                            {fmtMoney(l.line_total, r.currency)}
                                        </TableCell>
                                        <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                            {l.gl_account_number ? `${l.gl_account_number} — ${l.gl_account_name}` : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem' }}>
                            Approval History
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={labelCell}>When</TableCell>
                                    <TableCell sx={labelCell}>Action</TableCell>
                                    <TableCell sx={labelCell}>Actor</TableCell>
                                    <TableCell sx={labelCell}>Comment</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(r.approvals || []).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3,
                                                                     fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                            No actions yet
                                        </TableCell>
                                    </TableRow>
                                ) : r.approvals.map(a => (
                                    <TableRow key={a.id}>
                                        <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                            {a.action_at ? new Date(a.action_at).toLocaleString() : '—'}
                                        </TableCell>
                                        <TableCell sx={dataCell}>
                                            <Chip label={a.action} size="small"
                                                  sx={{ fontFamily, fontSize: '0.7rem', height: 20,
                                                        bgcolor: '#f1f5f9', color: '#475569' }} />
                                        </TableCell>
                                        <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                            {a.actor_email || `User #${a.actor_user_id}`}
                                        </TableCell>
                                        <TableCell sx={{ ...dataCell, fontSize: '0.82rem' }}>
                                            {a.comment || '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            {/* Reject dialog */}
            <Dialog open={rejectDialog.open}
                    onClose={() => setRejectDialog({ open: false, reason: '' })}
                    PaperProps={{ sx: { borderRadius: '16px' } }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Reject Requisition</DialogTitle>
                <DialogContent>
                    <TextField autoFocus fullWidth multiline rows={3} label="Reason"
                               value={rejectDialog.reason}
                               onChange={e => setRejectDialog(prev => ({ ...prev, reason: e.target.value }))}
                               InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                               InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRejectDialog({ open: false, reason: '' })}
                            sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={submitReject}
                            sx={{ fontFamily, textTransform: 'none', bgcolor: '#ef4444',
                                  '&:hover': { bgcolor: '#dc2626' } }}>
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancel dialog */}
            <Dialog open={cancelDialog.open}
                    onClose={() => setCancelDialog({ open: false, reason: '' })}
                    PaperProps={{ sx: { borderRadius: '16px' } }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Cancel Requisition</DialogTitle>
                <DialogContent>
                    <TextField fullWidth multiline rows={2} label="Reason (optional)"
                               value={cancelDialog.reason}
                               onChange={e => setCancelDialog(prev => ({ ...prev, reason: e.target.value }))}
                               InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                               InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCancelDialog({ open: false, reason: '' })}
                            sx={{ fontFamily, textTransform: 'none' }}>Back</Button>
                    <Button variant="contained" onClick={submitCancel}
                            sx={{ fontFamily, textTransform: 'none', bgcolor: '#94a3b8',
                                  '&:hover': { bgcolor: '#64748b' } }}>
                        Cancel Requisition
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Convert dialog */}
            <Dialog open={convertDialog.open}
                    onClose={() => setConvertDialog({ ...convertDialog, open: false })}
                    PaperProps={{ sx: { borderRadius: '16px' } }} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Convert to Procurement PO</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                        <Autocomplete size="small"
                                      options={vendors}
                                      getOptionLabel={v => v ? (v.display_name || v.company_name || `Vendor #${v.id}`) : ''}
                                      value={vendors.find(v => v.id === parseInt(convertDialog.vendor_id, 10)) || null}
                                      onChange={(_, val) => setConvertDialog(p => ({ ...p, vendor_id: val?.id || '' }))}
                                      renderInput={p => <TextField {...p} label="Vendor *"
                                                                   InputLabelProps={{ ...p.InputLabelProps, sx: { fontFamily, fontSize: '0.85rem' } }}
                                                                   InputProps={{ ...p.InputProps,
                                                                                 sx: { fontFamily, fontSize: '0.85rem', borderRadius: '10px' } }} />} />
                        <TextField label="Expected Delivery Date" type="date" size="small"
                                   value={convertDialog.expected_delivery_date}
                                   onChange={e => setConvertDialog(p => ({ ...p, expected_delivery_date: e.target.value }))}
                                   InputLabelProps={{ shrink: true, sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '10px' } }} />
                        <FormControl size="small">
                            <InputLabel sx={{ fontFamily, fontSize: '0.85rem' }}>Payment Terms</InputLabel>
                            <Select value={convertDialog.payment_terms}
                                    label="Payment Terms"
                                    onChange={e => setConvertDialog(p => ({ ...p, payment_terms: e.target.value }))}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {['DUE_ON_RECEIPT', 'NET_15', 'NET_30', 'NET_60', 'NET_90'].map(t =>
                                    <MenuItem key={t} value={t} sx={{ fontFamily }}>{t.replace('_', ' ')}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConvertDialog({ ...convertDialog, open: false })}
                            sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={submitConvert}
                            sx={{ fontFamily, textTransform: 'none', bgcolor: '#3b82f6',
                                  '&:hover': { bgcolor: '#2563eb' } }}>
                        Convert
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
